import { ComponentChildren } from "preact";
import { type Credentials, setName } from "@alice-and-bot/core";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "preact/hooks";
import { posthog } from "posthog-js";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { toast } from "react-hot-toast";
import {
  type BotToggleField,
  botToggles,
  type BotToggleSpec,
} from "../../botToggles.ts";
import {
  apiClient,
  defaultErrorMessage,
  GroupChatBehavior,
  groupChatBehaviorOptions,
  type PreferredProvider,
  preferredProviderOptions,
} from "../../constants.ts";
import {
  promptGuide,
  promptSecurityNote,
  statelessPromptGuide,
} from "../../promptGuidelines.ts";
import {
  Button,
  codeChildProps,
  Collapsible,
  customConfirm,
  inlineCodeClass,
  inputBaseClass,
  inputClass,
} from "./components.tsx";
import { transact, tx, useAuth } from "./db.ts";

const validatePrompt = (token: string, prompt: string, botId?: string) =>
  apiClient({
    endpoint: "validate-prompt",
    token,
    payload: { prompt, ...(botId ? { botId } : {}) },
  });

export const remoteChangedMessage =
  "The prompt has been changed remotely. Do you want to discard your local changes and load the new prompt?";

export const defaultPromptValidationErrorMessage =
  "A bot prompt must be stateless: it defines static personality and instructions, not changing memory or scheduled jobs. It cannot contain scheduled tasks (like 'send a daily reminder' - configure those under Scheduled Tasks), running lists to update during conversations, or instructions to rewrite the prompt over time.";

export const noChangesToSaveMessage = "No changes to save";

export const useSyncedPrompt = (remote: string) => {
  const [everChangedLocally, setEverChangedLocally] = useState(false);
  const [local, setLocal] = useState(remote);
  const prevRemote = useRef(remote);

  useEffect(() => {
    if (remote !== prevRemote.current) {
      prevRemote.current = remote;
      if (remote !== local) {
        if (!everChangedLocally) {
          setLocal(remote);
        } else {
          customConfirm(remoteChangedMessage).then((confirmed) => {
            if (confirmed) {
              setEverChangedLocally(false);
              setLocal(remote);
            }
          });
        }
      } else {
        setEverChangedLocally(false);
      }
    }
  }, [remote, everChangedLocally, local]);

  return [
    local,
    (value: string) => {
      if (value === local) return;
      setEverChangedLocally(true);
      setLocal(value);
    },
  ] as const;
};

export const PromptGuide = () => (
  <Collapsible title="How to write a good prompt">
    <div className="text-sm text-text-secondary-light dark:text-text-secondary space-y-3">
      <div>
        {promptGuide.intro}
        <ul className="mt-1 ml-4 list-disc">
          {promptGuide.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
        </ul>
      </div>
      <div>
        <strong>Important:</strong> {promptGuide.important}
        <br />
        {promptGuide.closing}
      </div>
      <div>
        <strong>Security Notice:</strong> {promptSecurityNote}
      </div>
    </div>
  </Collapsible>
);

export type { BotToggleField };

export type ToggleMessages = {
  enabled: string;
  disabled: string;
};

export const readableError = (error: unknown) =>
  error && typeof error === "object" && "message" in error
    ? String((error as { message?: string }).message)
    : String(error);

export const identity = <T,>(value: T) => value;

export const persistBotToggle = (
  botId: string,
  field: BotToggleField,
) =>
(value: boolean) => transact(tx.bots[botId].update({ [field]: value }));

export const makeToggleHandler = (
  {
    persist,
    messages,
    setEditable,
    setSaving,
    revertValue,
    mapInput,
  }: {
    persist: (value: boolean) => Promise<unknown>;
    messages: ToggleMessages;
    setEditable: (value: boolean) => void;
    setSaving: (value: boolean) => void;
    revertValue: boolean;
    mapInput: (value: boolean) => boolean;
  },
) =>
async (input: boolean) => {
  const desiredValue = mapInput(input);
  try {
    setSaving(true);
    setEditable(desiredValue);
    await persist(desiredValue);
    toast.success(desiredValue ? messages.enabled : messages.disabled);
  } catch (error) {
    toast.error("Error: " + readableError(error));
    setEditable(revertValue);
  } finally {
    setSaving(false);
  }
};

const computeLineDiff = (oldText: string, newText: string) => {
  const oldLines = oldText.split("\n");
  const newLines = newText.split("\n");
  const m = oldLines.length;
  const n = newLines.length;
  const dp: number[][] = Array.from(
    { length: m + 1 },
    () => Array(n + 1).fill(0),
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = oldLines[i - 1] === newLines[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const result: { type: "same" | "added" | "removed"; text: string }[] = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      result.unshift({ type: "same", text: oldLines[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ type: "added", text: newLines[j - 1] });
      j--;
    } else {
      result.unshift({ type: "removed", text: oldLines[i - 1] });
      i--;
    }
  }
  return result;
};

const diffLineClass = {
  same: "text-text-secondary-light dark:text-text-secondary",
  added: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300",
  removed:
    "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 line-through",
};

const diffPrefix = { same: " ", added: "+", removed: "-" } as const;

const HistoryEntry = ({
  entry,
  currentPrompt,
  onRestore,
}: {
  entry: { content: string; savedAt: number };
  currentPrompt: string;
  onRestore: (content: string) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const diff = expanded ? computeLineDiff(entry.content, currentPrompt) : [];
  const hasChanges = expanded && diff.some((d) => d.type !== "same");

  return (
    <div className="border-b border-border-subtle-light dark:border-border-subtle last:border-0">
      <div
        onClick={() => setExpanded(!expanded)}
        className="p-3 hover:bg-surface-overlay-light dark:hover:bg-surface-overlay cursor-pointer flex justify-between items-center gap-4"
      >
        <div className="flex-1 min-w-0">
          <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
            {DateTime.fromMillis(entry.savedAt).toLocaleString(
              DateTime.DATETIME_MED,
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            disabled={entry.content === currentPrompt}
            onClick={async (e: Event) => {
              e.stopPropagation();
              if (
                await customConfirm(
                  "Are you sure you want to restore this version? Current changes will be lost.",
                )
              ) {
                onRestore(entry.content);
              }
            }}
            className="text-xs py-1 px-2 h-auto"
          >
            Restore
          </Button>
          <span className="text-text-tertiary-light dark:text-text-tertiary text-xs">
            {expanded ? "\u25B2" : "\u25BC"}
          </span>
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3">
          {hasChanges
            ? (
              <pre className="text-xs font-mono leading-5 overflow-x-auto rounded-lg border border-border-subtle-light dark:border-border-subtle p-3 bg-surface-light dark:bg-surface-raised">
                {diff.map((line, i) => (
                  <div key={i} className={diffLineClass[line.type]}>
                    {diffPrefix[line.type]} {line.text}
                  </div>
                ))}
              </pre>
            )
            : (
              <div className="text-xs text-text-tertiary-light dark:text-text-tertiary italic">
                No changes from current prompt
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export const PromptHistory = ({
  history,
  currentPrompt,
  onRestore,
}: {
  history: { content: string; savedAt: number }[] | undefined;
  currentPrompt: string;
  onRestore: (content: string) => void;
}) => {
  if (!history || history.length === 0) return null;

  return (
    <Collapsible title="Prompt history">
      <div className="max-h-80 overflow-y-auto">
        {history
          .slice()
          .sort((a, b) => b.savedAt - a.savedAt)
          .map((entry) => (
            <HistoryEntry
              key={entry.savedAt}
              entry={entry}
              currentPrompt={currentPrompt}
              onRestore={onRestore}
            />
          ))}
      </div>
    </Collapsible>
  );
};

export const BotNameEditor = (
  { botId, name, aliceAndBotCredentials }: {
    botId: string;
    name: string;
    aliceAndBotCredentials?: Credentials;
  },
) => {
  const [editableName, setEditableName] = useState(name);
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    setEditableName(name);
  }, [name]);

  const isNameDirty = editableName !== name;

  const handleSaveName = async () => {
    if (!isNameDirty) return;
    try {
      setSavingName(true);
      await transact(tx.bots[botId].update({ name: editableName }));
      if (aliceAndBotCredentials?.publicSignKey) {
        setName({
          credentials: aliceAndBotCredentials,
          name: editableName,
        }).catch((err) =>
          console.warn("Failed to update alice-and-bot name", err)
        );
      }
      toast.success("Name saved");
    } catch (err) {
      toast.error("Error: " + readableError(err));
    } finally {
      setSavingName(false);
    }
  };

  return (
    <div>
      <label>Name</label>
      <div className="flex items-center gap-2">
        <input
          dir="auto"
          value={editableName}
          onChange={(e) => setEditableName(e.currentTarget.value)}
          className={`flex-1 max-w-md ${inputBaseClass}`}
        />
        <Button disabled={!isNameDirty || savingName} onClick={handleSaveName}>
          {savingName ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
};

export const BotErrorMessageEditor = ({
  botId,
  errorMessage,
}: {
  botId: string;
  errorMessage: string | undefined;
}) => {
  const [draft, setDraft] = useState(errorMessage ?? "");

  useEffect(() => {
    setDraft(errorMessage ?? "");
  }, [errorMessage]);

  const isDirty = draft !== (errorMessage ?? "");

  return (
    <div>
      <label>Error Message</label>
      <div className="text-xs text-text-tertiary-light dark:text-text-tertiary mb-1">
        Message shown to users when the bot encounters an error.
      </div>
      <div className="flex items-center gap-2">
        <input
          dir="auto"
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          placeholder={defaultErrorMessage}
          className={`flex-1 max-w-md ${inputBaseClass}`}
        />
        <Button
          disabled={!isDirty}
          onClick={() =>
            toast.promise(
              transact(
                tx.bots[botId].update({
                  errorMessage: draft || undefined,
                }),
              ),
              {
                loading: "Saving...",
                success: "Error message saved",
                error: (e) => `Error: ${e.message}`,
              },
            )}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export const BotDeprecationNoticeEditor = ({
  botId,
  deprecationNotice,
}: {
  botId: string;
  deprecationNotice: string | undefined;
}) => {
  const [draft, setDraft] = useState(deprecationNotice ?? "");

  useEffect(() => {
    setDraft(deprecationNotice ?? "");
  }, [deprecationNotice]);

  const isDirty = draft !== (deprecationNotice ?? "");

  return (
    <div>
      <label>Deprecation Notice</label>
      <div className="text-xs text-text-tertiary-light dark:text-text-tertiary mb-1">
        If set, the bot sends this notice to anyone who messages it instead of
        running the agent.
      </div>
      <textarea
        dir="auto"
        rows={5}
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
        placeholder="Leave empty to keep the bot active."
        className={`w-full max-w-full ${inputBaseClass}`}
      />
      <div className="mt-2">
        <Button
          disabled={!isDirty}
          onClick={() =>
            toast.promise(
              transact(
                tx.bots[botId].update({
                  deprecationNotice: draft.trim() || undefined,
                }),
              ),
              {
                loading: "Saving...",
                success: draft.trim()
                  ? "Deprecation notice saved"
                  : "Deprecation notice cleared",
                error: (e) => `Error: ${e.message}`,
              },
            )}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

const promptMarkdownComponents = {
  p: ({ children }: { children?: ComponentChildren }) => (
    <p className="mb-2 last:mb-0 leading-relaxed text-text-primary-light dark:text-text-primary">
      {children}
    </p>
  ),
  ul: ({ children }: { children?: ComponentChildren }) => (
    <ul className="list-disc pl-5 mb-2 space-y-1 last:mb-0 text-text-primary-light dark:text-text-primary">
      {children}
    </ul>
  ),
  ol: ({ children }: { children?: ComponentChildren }) => (
    <ol className="list-decimal pl-5 mb-2 space-y-1 last:mb-0 text-text-primary-light dark:text-text-primary">
      {children}
    </ol>
  ),
  li: ({ children }: { children?: ComponentChildren }) => <li>{children}</li>,
  h1: ({ children }: { children?: ComponentChildren }) => (
    <h1 className="text-lg font-bold mt-3 mb-1.5 first:mt-0 text-text-primary-light dark:text-text-primary">
      {children}
    </h1>
  ),
  h2: ({ children }: { children?: ComponentChildren }) => (
    <h2 className="text-base font-bold mt-2.5 mb-1 first:mt-0 text-text-primary-light dark:text-text-primary">
      {children}
    </h2>
  ),
  h3: ({ children }: { children?: ComponentChildren }) => (
    <h3 className="text-sm font-semibold mt-2 mb-1 first:mt-0 text-text-primary-light dark:text-text-primary">
      {children}
    </h3>
  ),
  h4: ({ children }: { children?: ComponentChildren }) => (
    <h4 className="text-xs font-semibold mt-1.5 mb-0.5 first:mt-0 text-text-primary-light dark:text-text-primary">
      {children}
    </h4>
  ),
  blockquote: ({ children }: { children?: ComponentChildren }) => (
    <blockquote className="border-l-2 border-border-default-light dark:border-border-default pl-3 my-2 text-text-secondary-light dark:text-text-secondary italic">
      {children}
    </blockquote>
  ),
  pre: ({ children }: { children?: ComponentChildren }) => {
    const { children: code = "" } = codeChildProps(children);
    return (
      <pre className="overflow-auto text-xs p-3 my-2 bg-surface-light dark:bg-surface-raised rounded-lg border border-border-subtle-light dark:border-border-subtle font-mono leading-5">
        <code>{String(code)}</code>
      </pre>
    );
  },
  code: ({ children }: { children?: ComponentChildren }) => (
    <code className={inlineCodeClass}>
      {children}
    </code>
  ),
  a: (
    { href, children }: { href?: string; children?: ComponentChildren },
  ) => (
    <a
      href={href}
      className="text-accent-light dark:text-accent underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  strong: ({ children }: { children?: ComponentChildren }) => (
    <strong className="font-semibold text-text-primary-light dark:text-text-primary">
      {children}
    </strong>
  ),
  em: ({ children }: { children?: ComponentChildren }) => (
    <em className="italic">{children}</em>
  ),
};

export const saveBotPrompt = async ({
  token,
  botId,
  editablePrompt,
  currentPrompt,
  promptHistory,
  setValidationError,
}: {
  token: string;
  botId: string;
  editablePrompt: string;
  currentPrompt: string;
  promptHistory: { content: string; savedAt: number }[] | undefined;
  setValidationError: (error: string) => void;
}) => {
  if (!token) {
    throw new Error("Authentication token not ready. Please try again.");
  }
  const { isValid, explanation } = await validatePrompt(
    token,
    editablePrompt,
    botId,
  );
  if (!isValid) {
    const errorMsg = explanation.trim() || defaultPromptValidationErrorMessage;
    setValidationError(errorMsg);
    posthog.capture("Prompt Validation Failed", {
      botId,
      prompt: editablePrompt,
      explanation: errorMsg,
    });
    throw new Error(errorMsg);
  }
  posthog.capture("Prompt Validated", {
    botId,
    prompt: editablePrompt,
  });
  const newHistory = [
    ...(promptHistory || []),
    { content: currentPrompt || "", savedAt: Date.now() },
  ].slice(-100);
  await transact(
    tx.bots[botId].update({
      prompt: editablePrompt,
      promptHistory: newHistory,
    }),
  );
  setValidationError("");
};

export const BotPromptEditor = ({
  botId,
  prompt,
  promptHistory,
}: {
  botId: string;
  prompt: string;
  promptHistory: { content: string; savedAt: number }[] | undefined;
}) => {
  const [editablePrompt, setEditablePrompt] = useSyncedPrompt(prompt || "");
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [validationError, setValidationError] = useState("");
  const token = useAuth().user?.refresh_token ?? "";

  const isPromptDirty = editablePrompt !== (prompt || "");

  const handleSavePrompt = async () => {
    if (!isPromptDirty) {
      toast(noChangesToSaveMessage);
      return;
    }
    setSavingPrompt(true);
    setValidationError("");
    try {
      await toast.promise(
        saveBotPrompt({
          token,
          botId,
          editablePrompt,
          currentPrompt: prompt || "",
          promptHistory,
          setValidationError,
        }),
        {
          loading: "Validating and saving prompt...",
          success: "Prompt saved",
          error: (err) => readableError(err),
        },
      );
    } finally {
      setSavingPrompt(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
          This saved prompt is the default. A custom tool named{" "}
          <code>remote_config</code>{" "}
          can override it per conversation at runtime.
        </div>
        <Button
          onClick={() => setIsEditing(!isEditing)}
          className="text-xs py-1 px-2 h-auto"
        >
          {isEditing ? "Preview" : "Edit"}
        </Button>
      </div>
      {isEditing
        ? (
          <textarea
            dir="auto"
            rows={10}
            placeholder="Describe your bot's personality, behaviour and goals."
            value={editablePrompt}
            onInput={(e) => {
              setValidationError("");
              setEditablePrompt(e.currentTarget.value);
            }}
            onChange={(e) => {
              setValidationError("");
              setEditablePrompt(e.currentTarget.value);
            }}
            className={`w-full max-w-full ${inputBaseClass}`}
          />
        )
        : (
          <div
            dir="auto"
            className={`w-full max-w-full ${inputBaseClass} min-h-[120px]`}
          >
            {editablePrompt
              ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkBreaks]}
                  // @ts-expect-error: Preact VNode vs React ReactNode mismatch
                  components={promptMarkdownComponents}
                >
                  {editablePrompt}
                </ReactMarkdown>
              )
              : (
                <span className="text-text-tertiary-light dark:text-text-tertiary italic">
                  No prompt set yet. Click Edit to add one.
                </span>
              )}
          </div>
        )}
      <div className="mt-2 mb-2 flex gap-2 items-center">
        <Button
          disabled={!isPromptDirty || savingPrompt}
          onClick={handleSavePrompt}
        >
          {savingPrompt ? "Saving..." : "Save"}
        </Button>
        {isPromptDirty && (
          <Button
            onClick={() => {
              setValidationError("");
              setEditablePrompt(prompt || "");
            }}
          >
            Discard
          </Button>
        )}
      </div>
      {validationError && (
        <div className="mb-3 rounded-lg border border-danger/40 bg-danger/10 p-3 text-sm text-danger space-y-2">
          <div className="font-semibold">Prompt validation failed</div>
          <div className="whitespace-pre-wrap">{validationError}</div>
          <div className="pt-2 border-t border-danger/20 text-xs text-text-secondary-light dark:text-text-secondary">
            <div className="font-semibold text-text-primary-light dark:text-text-primary mb-1">
              {statelessPromptGuide.title}
            </div>
            <div>{statelessPromptGuide.summary}</div>
            <ul className="mt-1 ml-4 list-disc space-y-0.5">
              {statelessPromptGuide.rules.map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      <PromptHistory
        history={promptHistory}
        currentPrompt={editablePrompt}
        onRestore={(content) => {
          setEditablePrompt(content);
          setIsEditing(true);
        }}
      />
      <PromptGuide />
    </>
  );
};

export const BotTimezoneEditor = ({
  botId,
  timezoneIANA,
}: {
  botId: string;
  timezoneIANA: string | undefined;
}) => {
  const [editableTimezone, setEditableTimezone] = useState<string>(
    timezoneIANA || "UTC",
  );
  const [timezoneError, setTimezoneError] = useState<string>("");
  const [savingTimezone, setSavingTimezone] = useState(false);

  useEffect(() => {
    setEditableTimezone(timezoneIANA || "UTC");
  }, [timezoneIANA]);

  const currentTz = timezoneIANA || "UTC";
  const isTimezoneDirty = editableTimezone !== currentTz;

  const handleSaveTimezone = async () => {
    if (!isTimezoneDirty || timezoneError) return;
    try {
      setSavingTimezone(true);
      await transact(tx.bots[botId].update({ timezoneIANA: editableTimezone }));
      toast.success("Timezone saved");
    } catch (err) {
      toast.error("Error: " + readableError(err));
    } finally {
      setSavingTimezone(false);
    }
  };

  return (
    <div className="mb-4 mt-4">
      <label>
        Timezone (IANA name, e.g. "America/New_York", "Asia/Jerusalem")
      </label>
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="e.g. America/New_York, Asia/Jerusalem, UTC"
          value={editableTimezone}
          onChange={({ currentTarget: { value } }) => {
            setEditableTimezone(value);
            const { isValid } = DateTime.now().setZone(value);
            setTimezoneError(isValid ? "" : "Invalid timezone");
          }}
          className={`flex-1 max-w-md ${inputBaseClass}`}
        />
        <Button
          disabled={!isTimezoneDirty || !!timezoneError || savingTimezone}
          onClick={handleSaveTimezone}
        >
          {savingTimezone ? "Saving..." : "Save"}
        </Button>
      </div>
      {editableTimezone && !timezoneError && (
        <div className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-1">
          Offset now: GMT
          {DateTime.now().setZone(editableTimezone).toFormat(" ZZZZ")}
        </div>
      )}
      {timezoneError && (
        <div className="text-xs text-danger mt-1">
          {timezoneError}
        </div>
      )}
    </div>
  );
};

const ToggleSetting = ({
  id,
  checked,
  disabled,
  onChange,
  label,
  description,
}: {
  id: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description: string;
}) => (
  <>
    <div className="flex items-center gap-3 py-1">
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${
          checked ? "bg-accent" : "bg-gray-300 dark:bg-gray-700"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
      <label
        htmlFor={id}
        className="text-sm cursor-pointer select-none font-medium text-text-primary-light dark:text-text-primary"
        onClick={() => !disabled && onChange(!checked)}
      >
        {label}
      </label>
    </div>
    <div className="text-xs text-text-tertiary-light dark:text-text-tertiary mb-4 pl-14">
      {description}
    </div>
  </>
);

const useBotToggle = (
  botId: string,
  field: BotToggleField,
  value: boolean | undefined,
  messages: ToggleMessages,
  {
    mapInput = identity,
    mapValue = identity,
  }: {
    mapInput?: (value: boolean) => boolean;
    mapValue?: (value: boolean) => boolean;
  } = {},
) => {
  const [localValue, setLocalValue] = useState<boolean>(!!value);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalValue(!!value);
  }, [value]);

  const onChange = makeToggleHandler({
    persist: persistBotToggle(botId, field),
    messages,
    setEditable: setLocalValue,
    setSaving,
    revertValue: !!value,
    mapInput,
  });

  return {
    checked: mapValue(localValue),
    disabled: saving,
    onChange,
  };
};

export type { GroupChatBehavior };

export const GroupChatBehaviorSelector = ({
  botId,
  groupChatBehavior,
}: {
  botId: string;
  groupChatBehavior: string | undefined;
}) => {
  const current = (groupChatBehavior ?? "mentions") as GroupChatBehavior;

  return (
    <div className="mb-4">
      <label className="text-sm">Group chat behavior</label>
      <div className="text-xs text-text-tertiary-light dark:text-text-tertiary mb-1">
        Controls how the bot responds in group conversations.
      </div>
      <select
        value={current}
        onChange={(e) =>
          toast.promise(
            transact(
              tx.bots[botId].update({
                groupChatBehavior: e.currentTarget.value,
              }),
            ),
            {
              loading: "Saving...",
              success: "Group chat behavior saved",
              error: (err) => `Error: ${readableError(err)}`,
            },
          )}
        className={`${inputClass} text-sm`}
      >
        {groupChatBehaviorOptions.map(({ value, label }) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>
    </div>
  );
};

export const PreferredProviderSelector = ({
  botId,
  preferredProvider,
}: {
  botId: string;
  preferredProvider: string | undefined;
}) => {
  const current = (preferredProvider ?? "google") as PreferredProvider;

  return (
    <div className="mb-4">
      <label className="text-sm">AI Provider</label>
      <div className="text-xs text-text-tertiary-light dark:text-text-tertiary mb-1">
        Which AI provider powers this bot.
      </div>
      <select
        value={current}
        onChange={(e) =>
          toast.promise(
            transact(
              tx.bots[botId].update({
                preferredProvider: e.currentTarget.value,
              }),
            ),
            {
              loading: "Saving...",
              success: "AI provider saved",
              error: (err) => `Error: ${readableError(err)}`,
            },
          )}
        className={`${inputClass} text-sm`}
      >
        {preferredProviderOptions.map(({ value, label }) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>
    </div>
  );
};

const negate = (v: boolean) => !v;

const BotToggle = (
  { botId, spec, value }: {
    botId: string;
    spec: BotToggleSpec;
    value: boolean | undefined;
  },
) => {
  const toggle = useBotToggle(
    botId,
    spec.field,
    value ?? spec.defaultTrue,
    { enabled: spec.toastFieldTrue, disabled: spec.toastFieldFalse },
    spec.inverted ? { mapInput: negate, mapValue: negate } : {},
  );
  const fieldIsTrue = toggle.checked !== !!spec.inverted;
  return (
    <ToggleSetting
      id={`${spec.field}-toggle`}
      label={spec.label}
      description={fieldIsTrue
        ? spec.fieldTrueDescription
        : spec.fieldFalseDescription}
      {...toggle}
    />
  );
};

export const BotSettingsToggles = (
  { botId, toggleValues, fields }: {
    botId: string;
    toggleValues: { [K in BotToggleField]?: boolean };
    fields?: BotToggleField[];
  },
) => (
  <>
    {botToggles
      .filter((spec) => !fields || fields.includes(spec.field))
      .map((spec) => (
        <BotToggle
          key={spec.field}
          botId={botId}
          spec={spec}
          value={toggleValues[spec.field]}
        />
      ))}
  </>
);

const blockedUsersToText = (blockedUsers: string[] | undefined) =>
  (blockedUsers ?? []).join("\n");

const textToBlockedUsers = (text: string) =>
  text.split("\n").map((s) => s.trim()).filter(Boolean);

export const BlockedUsersEditor = ({
  botId,
  blockedUsers,
}: {
  botId: string;
  blockedUsers: string[] | undefined;
}) => {
  const remoteText = blockedUsersToText(blockedUsers);
  const [draft, setDraft] = useState(remoteText);

  useEffect(() => {
    setDraft(remoteText);
  }, [remoteText]);

  const isDirty = draft !== remoteText;

  return (
    <div className="mb-4">
      <label>Blocked Users</label>
      <div className="text-xs text-text-tertiary-light dark:text-text-tertiary mb-1">
        One user ID per line. Blocked users will not receive any response from
        the bot.
      </div>
      <textarea
        dir="auto"
        rows={4}
        placeholder="One user ID per line"
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
        className={`w-full max-w-full ${inputBaseClass} font-mono text-sm`}
      />
      <div className="mt-2">
        <Button
          disabled={!isDirty}
          onClick={() =>
            toast.promise(
              transact(
                tx.bots[botId].update({
                  blockedUsers: textToBlockedUsers(draft),
                }),
              ),
              {
                loading: "Saving...",
                success: "Blocked users saved",
                error: (e) => `Error: ${e.message}`,
              },
            )}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
